import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { companyAPI } from '../lib/api';
import api from '../lib/api';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ClosureDaysManager from './companysettings/ClosureDaysManager';
import { BrandingCard } from './companysettings/BrandingCard';
import { ColorsCard } from './companysettings/ColorsCard';
import { ContactCard } from './companysettings/ContactCard';
import { LegalCard } from './companysettings/LegalCard';
import { PrivacyCard } from './companysettings/PrivacyCard';
import { DATEVSettingsCard } from './companysettings/DATEVSettingsCard';
import { OrgLabelsCard } from './companysettings/OrgLabelsCard';
import { ApprovalChainCard } from './companysettings/ApprovalChainCard';
import { OrgStructureCard } from './companysettings/OrgStructureCard';
import { DatabaseExportCard } from './companysettings/DatabaseExportCard';

const initialFormState = {
  name: '', logo_url: '',
  primary_color: '#14532D', secondary_color: '#65A30D',
  address: '', city: '', postal_code: '', country: 'Deutschland',
  phone: '', email: '', website: '',
  tax_id: '', registration_number: '', managing_director: '',
  terminal_display_mode: 1,
  holiday_auto_credit: true,
  free_christmas_eve: true,
  free_new_years_eve: true,
  ko_tag_enabled: true,
  deduct_absences_from_target: true,
  session_timeout_minutes: 30,
  jwt_expiration_hours: 24,
  reminder_threshold_minutes: 10,
  pre_shift_reminder_enabled: true,
  pre_shift_reminder_minutes: 15,
  end_shift_reminder_enabled: true,
  end_shift_reminder_minutes: 5,
  contract_expiry_reminder_value: 30,
  contract_expiry_reminder_unit: 'days',
  fulltime_threshold_hours: 39,
};

export default function CompanySettings() {
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await companyAPI.get();
        // Backend liefert für leere String-Felder null — React beschwert sich
        // sonst mit "value prop on input should not be null". Wir glaetten
        // alle null/undefined String-Felder auf '' bevor sie in den State gehen.
        const sanitized = Object.fromEntries(
          Object.entries(response.data || {}).map(([k, v]) => [k, v === null ? '' : v])
        );
        setFormData({
          ...initialFormState,
          ...sanitized,
          terminal_display_mode: sanitized.terminal_display_mode || 1,
          holiday_auto_credit: sanitized.holiday_auto_credit !== false,
          free_christmas_eve: sanitized.free_christmas_eve !== false,
          free_new_years_eve: sanitized.free_new_years_eve !== false,
          ko_tag_enabled: sanitized.ko_tag_enabled !== false,
          deduct_absences_from_target: sanitized.deduct_absences_from_target !== false,
        });
      } catch (error) {
        console.error('Failed to load company settings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Bitte Firmennamen eingeben');
      return;
    }
    setSaving(true);
    try {
      await companyAPI.update(formData);
      toast.success('Firmeneinstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Nur PNG, JPG, SVG oder WebP Dateien erlaubt');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 2MB)');
      return;
    }
    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await api.post('/company/logo', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((prev) => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo erfolgreich hochgeladen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Möchten Sie das Logo wirklich löschen?')) return;
    try {
      await api.delete('/company/logo');
      setFormData((prev) => ({ ...prev, logo_url: '' }));
      toast.success('Logo gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[600px] rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="company-settings-page">
        <div>
          <h1 className="font-heading font-bold text-3xl text-foreground">Firmeneinstellungen</h1>
          <p className="text-muted-foreground mt-1">
            Individualisieren Sie das Erscheinungsbild Ihrer Firma
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <BrandingCard
                formData={formData}
                onChange={handleChange}
                onLogoUpload={handleLogoUpload}
                onLogoDelete={handleLogoDelete}
                uploadingLogo={uploadingLogo}
              />
              <ColorsCard formData={formData} onChange={handleChange} />
              <ContactCard formData={formData} onChange={handleChange} />
            </div>

            <div className="space-y-6">
              <LegalCard formData={formData} onChange={handleChange} />
              <PrivacyCard formData={formData} onChange={handleChange} />

              <Button
                type="submit"
                className="w-full rounded-full h-12"
                disabled={saving}
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird gespeichert...</>
                ) : (
                  <><Save className="mr-2 h-4 w-4" />Einstellungen speichern</>
                )}
              </Button>
            </div>
          </div>
        </form>

        <ClosureDaysManager />

        <OrgLabelsCard />

        <OrgStructureCard />

        <ApprovalChainCard />

        <DATEVSettingsCard />

        <DatabaseExportCard />
      </div>
    </DashboardLayout>
  );
}
